import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, Send } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { buildPdfSpec } from './reportPrint';

const API = `${window.__BACKEND_URL__}/api`;
const inputCls = 'w-full px-3 py-2 rounded-lg text-sm bg-[#0F1923] border border-[#2D3748] text-white';

// "Send this PDF" — pick staff and/or type emails; the server builds the branded PDF and emails it.
export default function EmailPdfDialog({ open, onClose, token, spec }) {
  const { toast } = useToast();
  const [staff, setStaff] = useState([]);
  const [picked, setPicked] = useState([]);
  const [extra, setExtra] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!open) return;
    setResult(null);
    axios.get(`${API}/users`, auth).then(({ data }) => {
      setStaff((Array.isArray(data) ? data : []).filter((u) => ['admin', 'supervisor'].includes(u.role) && u.email && !u.deleted_at && u.is_active !== false));
    }).catch(() => setStaff([]));
  }, [open]); // eslint-disable-line

  const recipients = Array.from(new Set([...picked, ...extra.split(/[,;\s]+/).map((e) => e.trim().toLowerCase()).filter(Boolean)]));

  const send = async () => {
    if (!recipients.length) { toast({ title: 'Add at least one recipient', variant: 'destructive' }); return; }
    setBusy(true);
    try {
      const body = await buildPdfSpec(spec);
      const { data } = await axios.post(`${API}/reports/v2/email-pdf`, { ...body, recipients, message }, auth);
      setResult(data);
      toast({ title: `Sent to ${data.sent.length} recipient${data.sent.length === 1 ? '' : 's'}`, description: data.failed.length ? `Failed: ${data.failed.join(', ')}` : undefined, variant: data.failed.length ? 'destructive' : undefined });
    } catch (e) {
      toast({ title: 'Could not send', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="email-pdf-dialog">
        <DialogHeader>
          <DialogTitle>Send this PDF</DialogTitle>
          <DialogDescription>{spec.title}{spec.period ? ` · ${spec.period}` : ''} — branded PDF attached, logged under Schedule Email → Sent.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {staff.length > 0 && (
            <div>
              <p className="text-xs mb-1" style={{ color: '#A0AEC0' }}>Staff</p>
              <div className="max-h-32 overflow-y-auto space-y-1 rounded-lg p-2" style={{ background: '#0F1923', border: '1px solid #2D3748' }}>
                {staff.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 text-xs text-white cursor-pointer">
                    <input type="checkbox" checked={picked.includes(u.email.toLowerCase())} className="accent-sky-500" data-testid={`email-pdf-staff-${u.id}`}
                      onChange={(e) => setPicked((p) => (e.target.checked ? [...p, u.email.toLowerCase()] : p.filter((x) => x !== u.email.toLowerCase())))} />
                    {u.name} <span style={{ color: '#64748B' }}>· {u.email}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <label className="block">
            <span className="text-xs block mb-1" style={{ color: '#A0AEC0' }}>Other emails (comma-separated)</span>
            <input className={inputCls} value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="owner@example.com, cfo@example.com" data-testid="email-pdf-extra" />
          </label>
          <label className="block">
            <span className="text-xs block mb-1" style={{ color: '#A0AEC0' }}>Message (optional)</span>
            <textarea className={inputCls} rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="For Monday's operations meeting." data-testid="email-pdf-message" />
          </label>
          <p className="text-[11px]" style={{ color: '#64748B' }} data-testid="email-pdf-count">{recipients.length} recipient{recipients.length === 1 ? '' : 's'} · {spec.rows?.length || 0} rows</p>
          {result && <p className="text-xs" style={{ color: '#00C896' }} data-testid="email-pdf-result">Sent: {result.sent.join(', ') || '—'}{result.failed.length ? ` · Failed: ${result.failed.join(', ')}` : ''}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="email-pdf-close">Close</Button>
          <Button onClick={send} disabled={busy || !recipients.length} className="bg-[#1E88E5] text-white" data-testid="email-pdf-send">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />} Send PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
